import { alpineData } from "./alpine-data";

type FrequencyMode = "hourly" | "daily" | "weekly" | "monthly";
type MinuteOffset = 0 | 15 | 30 | 45;

interface SummaryTemplates {
  hourly: string;
  hourlyAllDay: string;
  hourlyOnce: string;
  hourlyOnceAllDay: string;
  daily: string;
  weekly: string;
  monthly: string;
}

export function scheduleBuilder(initialCron: string, timezone: string, previewURL: string) {
  return alpineData({
    mode: "daily" as FrequencyMode,
    minuteOffset: 0 as MinuteOffset,
    interval: 1,
    windowFrom: 0,
    windowTo: 23,
    selectedHours: [9] as number[],
    selectedDays: [1] as number[],
    selectedMonthDays: [1] as number[],
    lastDay: false,
    advancedMode: false,
    rawCron: "",
    previewURL,
    timezone,
    previewHTML: "",
    previewError: false,
    _debounceTimer: null as ReturnType<typeof setTimeout> | null,
    _abortController: null as AbortController | null,
    newHour: 9,

    init() {
      if (initialCron) {
        if (!this.parseCron(initialCron)) {
          this.advancedMode = true;
          this.rawCron = initialCron;
        }
      }
      this.updateHiddenInput();
      this.triggerPreview();

      this.$watch("mode", () => this.onStateChange());
      this.$watch("minuteOffset", () => this.onStateChange());
      this.$watch("interval", () => this.onStateChange());
      this.$watch("windowFrom", () => this.onStateChange());
      this.$watch("windowTo", () => this.onStateChange());
      this.$watch("selectedHours", () => this.onStateChange());
      this.$watch("selectedDays", () => this.onStateChange());
      this.$watch("selectedMonthDays", () => this.onStateChange());
      this.$watch("lastDay", () => this.onStateChange());
      this.$watch("rawCron", () => {
        if (this.advancedMode) this.onStateChange();
      });
    },

    onStateChange() {
      this.updateHiddenInput();
      this.triggerPreview();
    },

    generateCron(): string {
      if (this.advancedMode) return this.rawCron;
      const min = this.minuteOffset;
      switch (this.mode as FrequencyMode) {
        case "hourly": {
          const isFullDay = this.windowFrom === 0 && this.windowTo === 23;
          if (isFullDay) return `${min} */${this.interval} * * *`;
          return `${min} ${this.windowFrom}-${this.windowTo}/${this.interval} * * *`;
        }
        case "daily": {
          const hours = [...this.selectedHours].sort((a, b) => a - b).join(",");
          return `${min} ${hours || "9"} * * *`;
        }
        case "weekly": {
          const hours = [...this.selectedHours].sort((a, b) => a - b).join(",");
          const days = [...this.selectedDays].sort((a, b) => a - b).join(",");
          return `${min} ${hours || "9"} * * ${days || "1"}`;
        }
        case "monthly": {
          const hours = [...this.selectedHours].sort((a, b) => a - b).join(",");
          let domParts: string[] = [];
          if (this.selectedMonthDays.length > 0) {
            domParts = [...this.selectedMonthDays].sort((a, b) => a - b).map(String);
          }
          if (this.lastDay) domParts.push("L");
          const dom = domParts.length > 0 ? domParts.join(",") : "1";
          return `${min} ${hours || "10"} ${dom} * *`;
        }
      }
    },

    parseCron(expr: string): boolean {
      const parts = expr.trim().split(/\s+/);
      if (parts.length !== 5) return false;
      const [minStr, hourStr, domStr, monthStr, dowStr] = parts;
      if (monthStr !== "*") return false;
      const minute = parseInt(minStr, 10);
      if (isNaN(minute) || ![0, 15, 30, 45].includes(minute)) return false;
      this.minuteOffset = minute as MinuteOffset;

      if (dowStr === "*" && domStr === "*" && hourStr.includes("/")) {
        this.mode = "hourly";
        const rangeStep = hourStr.match(/^(\*|\d+-\d+)\/(\d+)$/);
        if (!rangeStep) return false;
        this.interval = parseInt(rangeStep[2], 10);
        if (rangeStep[1] === "*") {
          this.windowFrom = 0;
          this.windowTo = 23;
        } else {
          const [from, to] = rangeStep[1].split("-").map(Number);
          this.windowFrom = from;
          this.windowTo = to;
        }
        return true;
      }

      const hours = hourStr.split(",").map(Number);
      if (hours.some(isNaN)) return false;

      if (domStr === "*" && dowStr !== "*") {
        this.mode = "weekly";
        this.selectedHours = hours;
        this.selectedDays = dowStr.split(",").map(Number);
        if (this.selectedDays.some(isNaN)) return false;
        return true;
      }

      if (domStr === "*" && dowStr === "*") {
        this.mode = "daily";
        this.selectedHours = hours;
        return true;
      }

      if (domStr !== "*" && dowStr === "*") {
        this.mode = "monthly";
        this.selectedHours = hours;
        const domParts = domStr.split(",");
        this.selectedMonthDays = [];
        this.lastDay = false;
        for (const p of domParts) {
          if (p === "L") {
            this.lastDay = true;
          } else {
            const day = parseInt(p, 10);
            if (isNaN(day)) return false;
            this.selectedMonthDays.push(day);
          }
        }
        return true;
      }

      return false;
    },

    humanReadable(): string {
      const templates = this.getSummaryTemplates();
      const pad = (n: number) => n.toString().padStart(2, "0");
      switch (this.mode as FrequencyMode) {
        case "hourly": {
          const isFullDay = this.windowFrom === 0 && this.windowTo === 23;
          const isOnce = this.interval === 1;
          if (isFullDay) {
            return this.interpolate(isOnce ? templates.hourlyOnceAllDay : templates.hourlyAllDay, {
              Interval: this.interval.toString(),
              Minute: pad(this.minuteOffset),
            });
          }
          return this.interpolate(isOnce ? templates.hourlyOnce : templates.hourly, {
            Interval: this.interval.toString(),
            Minute: pad(this.minuteOffset),
            From: `${pad(this.windowFrom)}:00`,
            To: `${pad(this.windowTo)}:00`,
          });
        }
        case "daily":
          return this.interpolate(templates.daily, { Times: this.formatTimes() });
        case "weekly":
          return this.interpolate(templates.weekly, {
            Days: this.formatWeekdays(),
            Times: this.formatTimes(),
          });
        case "monthly":
          return this.interpolate(templates.monthly, {
            Days: this.formatMonthDays(),
            Times: this.formatTimes(),
          });
      }
    },

    formatList(items: string[]): string {
      const el = this.$el as HTMLElement;
      const sep = el.dataset.listSeparator ?? ", ";
      const finalSep = el.dataset.listFinalSeparator ?? " and ";
      if (items.length === 0) return "";
      if (items.length === 1) return items[0];
      if (items.length === 2) return items[0] + finalSep + items[1];
      return items.slice(0, -1).join(sep) + finalSep + items[items.length - 1];
    },

    formatTimes(): string {
      const pad = (n: number) => n.toString().padStart(2, "0");
      const times = [...this.selectedHours]
        .sort((a, b) => a - b)
        .map((h) => `${pad(h)}:${pad(this.minuteOffset)}`);
      return this.formatList(times);
    },

    formatWeekdays(): string {
      const el = this.$el as HTMLElement;
      const dayNames = [
        el.dataset.daySunLong || "Sunday",
        el.dataset.dayMonLong || "Monday",
        el.dataset.dayTueLong || "Tuesday",
        el.dataset.dayWedLong || "Wednesday",
        el.dataset.dayThuLong || "Thursday",
        el.dataset.dayFriLong || "Friday",
        el.dataset.daySatLong || "Saturday",
      ];
      const names = [...this.selectedDays].sort((a, b) => a - b).map((d) => dayNames[d]);
      return this.formatList(names);
    },

    formatMonthDays(): string {
      const el = this.$el as HTMLElement;
      const parts = [...this.selectedMonthDays].sort((a, b) => a - b).map((d) => d.toString());
      if (this.lastDay)
        parts.push(el.dataset.lastDayShort || el.dataset.lastDayLabel || "last day");
      return this.formatList(parts);
    },

    getSummaryTemplates(): SummaryTemplates {
      const el = this.$el as HTMLElement;
      return {
        hourly:
          el.dataset.summaryHourly || "Every %Interval% hours at :%Minute%, from %From% to %To%",
        hourlyAllDay: el.dataset.summaryHourlyAllDay || "Every %Interval% hours at :%Minute%",
        hourlyOnce: el.dataset.summaryHourlyOnce || "Hourly at :%Minute%, from %From% to %To%",
        hourlyOnceAllDay: el.dataset.summaryHourlyOnceAllDay || "Hourly at :%Minute%",
        daily: el.dataset.summaryDaily || "Daily at %Times%",
        weekly: el.dataset.summaryWeekly || "Every %Days% at %Times%",
        monthly: el.dataset.summaryMonthly || "Monthly on %Days% at %Times%",
      };
    },

    interpolate(template: string, vars: Record<string, string>): string {
      let result = template;
      for (const [key, value] of Object.entries(vars)) {
        result = result.replace(new RegExp(`%${key}%`, "g"), value);
      }
      return result;
    },

    triggerPreview() {
      if (this._debounceTimer) clearTimeout(this._debounceTimer);
      this._debounceTimer = setTimeout(() => this.fetchPreview(), 300);
    },

    async fetchPreview() {
      if (this._abortController) this._abortController.abort();
      this._abortController = new AbortController();
      const cron = this.generateCron();
      if (!cron) return;
      try {
        const csrfToken =
          document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";
        const resp = await fetch(this.previewURL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/html",
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({ cron_expression: cron, timezone: this.timezone }),
          signal: this._abortController.signal,
        });
        if (resp.ok) {
          this.previewHTML = await resp.text();
          this.previewError = false;
        } else {
          this.previewHTML = "";
          this.previewError = true;
        }
      } catch (e: any) {
        if (e.name !== "AbortError") {
          this.previewHTML = "";
          this.previewError = true;
        }
      }
    },

    updateHiddenInput() {
      const input = (this.$el as HTMLElement).querySelector<HTMLInputElement>(
        'input[type="hidden"][data-schedule-value]',
      );
      if (input) input.value = this.generateCron();
    },

    addHour() {
      const hour = Number(this.newHour);
      if (!isNaN(hour) && hour >= 0 && hour <= 23 && !this.selectedHours.includes(hour)) {
        this.selectedHours = [...this.selectedHours, hour].sort((a, b) => a - b);
      }
    },

    removeHour(hour: number) {
      if (this.selectedHours.length > 1) {
        this.selectedHours = this.selectedHours.filter((h: number) => h !== hour);
      }
    },

    toggleDay(day: number) {
      if (this.selectedDays.includes(day)) {
        if (this.selectedDays.length > 1) {
          this.selectedDays = this.selectedDays.filter((d: number) => d !== day);
        }
      } else {
        this.selectedDays = [...this.selectedDays, day].sort((a, b) => a - b);
      }
    },

    toggleMonthDay(day: number) {
      if (this.selectedMonthDays.includes(day)) {
        this.selectedMonthDays = this.selectedMonthDays.filter((d: number) => d !== day);
      } else {
        this.selectedMonthDays = [...this.selectedMonthDays, day].sort((a, b) => a - b);
      }
    },

    switchToAdvanced() {
      this.rawCron = this.generateCron();
      this.advancedMode = true;
    },

    switchToVisual() {
      if (this.parseCron(this.rawCron)) {
        this.advancedMode = false;
      }
    },

    isValid(): boolean {
      if (this.advancedMode) return this.rawCron.trim().split(/\s+/).length === 5;
      switch (this.mode as FrequencyMode) {
        case "hourly":
          return this.windowFrom <= this.windowTo;
        case "daily":
          return this.selectedHours.length > 0;
        case "weekly":
          return this.selectedHours.length > 0 && this.selectedDays.length > 0;
        case "monthly":
          return (
            this.selectedHours.length > 0 && (this.selectedMonthDays.length > 0 || this.lastDay)
          );
      }
    },
  });
}
